import React from 'react';

export interface IconProps {
  className?: string;
}

/**
 * Creates a wrapped version of an icon component that accepts a className prop.
 *
 * This function takes an icon function (like IconPlus) and returns a new component
 * that accepts a className prop and applies it to the underlying SVG element.
 * The returned component preserves the original function name for better debugging.
 *
 * @param iconFunction - Icon that returns an SVG root. May be `() => svg` or accept optional `{ className }`.
 * @returns A new component that accepts className and applies it to the SVG
 *
 * @example
 * ```tsx
 * // Original icon: IconPlus() returns <svg>...</svg>
 * // Wrapped icon: createWrappedIcon(IconPlus) returns a component that accepts className
 * const WrappedIconPlus = createWrappedIcon(IconPlus);
 *
 * // Usage:
 * <WrappedIconPlus className="h-6 w-6 text-blue-500" />
 * // Will show as "IconPlus" in React DevTools
 * ```
 */
export function createWrappedIcon(
  iconFunction: (props: IconProps) => React.ReactElement,
) {
  const WrappedIcon = function ({ className }: IconProps) {
    const iconElement = iconFunction({ className });

    return React.cloneElement(iconElement, {
      ...iconElement.props,
      className,
    });
  };

  // Preserve the original function name for better debugging
  Object.defineProperty(WrappedIcon, 'name', {
    value: iconFunction.name,
    configurable: true,
  });

  // Also set displayName for React DevTools
  WrappedIcon.displayName = iconFunction.name;

  return WrappedIcon;
}
